import React, { useEffect, useRef, useState } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export const LanguageSelector = ({ variant = "desktop" }) => {
  const { lang, setLang, langs } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = langs.find((l) => l.code === lang) || langs[0];

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (variant === "mobile") {
    return (
      <div className="border-t border-white/10 pt-6" data-testid="language-selector-mobile">
        <span className="flex items-center gap-2 text-[10px] uppercase tracking-overline text-white/35">
          <Globe size={13} strokeWidth={1.5} /> {{ it: "Lingua", en: "Language", de: "Sprache", es: "Idioma", fr: "Langue" }[lang]}
        </span>
        <div className="mt-4 flex flex-wrap gap-2.5">
          {langs.map((l) => (
            <button
              key={l.code}
              onClick={() => setLang(l.code)}
              data-testid={`lang-option-${l.code}-mobile`}
              className={`rounded-none border px-4 py-2 text-xs uppercase tracking-widest transition-colors duration-300 ${
                lang === l.code ? "border-gold bg-gold text-ink" : "border-white/20 text-white/70 hover:border-gold hover:text-gold"
              }`}
            >
              {l.short}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref} data-testid="language-selector">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="language-selector-toggle"
        aria-label="Select language"
        className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/70 transition-colors duration-300 hover:text-gold"
      >
        <Globe size={15} strokeWidth={1.5} className="text-gold" />
        <span>{current.short}</span>
        <ChevronDown size={13} strokeWidth={1.5} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="language-dropdown"
          className="absolute right-0 top-full z-50 mt-3 min-w-[160px] border border-gold/25 bg-ink/95 py-2 backdrop-blur-xl"
        >
          {langs.map((l) => (
            <button
              key={l.code}
              onClick={() => {
                setLang(l.code);
                setOpen(false);
              }}
              data-testid={`lang-option-${l.code}`}
              className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors duration-200 ${
                lang === l.code ? "text-gold" : "text-white/70 hover:bg-white/5 hover:text-white"
              }`}
            >
              <span className="tracking-wide">{l.label}</span>
              {lang === l.code && <Check size={14} strokeWidth={1.5} className="text-gold" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
